import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React from 'react';
import { DateRange } from 'react-day-picker';
import { Smartphone, Monitor, Tablet as TabletIcon, Tv2 } from 'lucide-react';
import { DevicesMetricsTable } from '@/components/metrics/devices/DevicesMetricsTable';
import { PremiumDonutChart } from '@/components/metrics/charts/PremiumDonutChart';
import { Card } from '@/components/ui/card';

interface DevicesTabContentProps {
  date?: DateRange | undefined;
  videoId: string | null;
  devicesData?: any;
  metricsEnhanced?: any;
}

interface DeviceData {
  type: 'desktop' | 'mobile' | 'tablet' | 'tv' | 'other';
  views: number;
  percentage: number;
}

// Mock data for devices
const devicesMockData: DeviceData[] = [
  { type: 'desktop', views: 15483, percentage: 58.2 },
  { type: 'mobile', views: 9247, percentage: 34.8 },
  { type: 'tablet', views: 1632, percentage: 6.1 },
  { type: 'tv', views: 245, percentage: 0.9 }
];

// Get device label for display
function getDeviceLabel(type: string): string {
  switch (type) {
    case 'desktop': return 'Desktop';
    case 'mobile': return 'Mobile';
    case 'tablet': return 'Tablet';
    case 'tv': return 'TV';
    default: return 'Outros';
  }
}

// Get device icon based on type
function getDeviceIcon(type: string) {
  const iconClass = "h-[18px] w-[18px] text-[#4B7BFF]";
  switch (type) {
    case 'desktop':
      return <Monitor className={iconClass} />;
    case 'mobile':
      return <Smartphone className={iconClass} />;
    case 'tablet':
      return <TabletIcon className={iconClass} />;
    case 'tv':
      return <Tv2 className={iconClass} />;
    default:
      return <Monitor className={iconClass} />;
  }
}

// Component for rendering device list
function DevicesList({ data }: { data: DeviceData[] }) {
  useLanguage();
  return (
    <div className="space-y-[12px]">
      {data.map((item, index) => (
        <div key={index} className="bg-white border border-[#E5E7EB] rounded-[10px] px-[18px] py-[14px] min-h-[54px] flex items-center justify-between hover:bg-[#F9FAFB] transition-all duration-150 ease-out cursor-default">
          <div className="flex items-center gap-[10px]">
            {getDeviceIcon(item.type)}
            <div>
              <p className="text-[14px] font-medium text-[#0F172A] capitalize">{getDeviceLabel(item.type)}</p>
              <p className="text-[13px] font-normal text-[#6B7280]">{item.percentage}{translateInterfaceText("% das visualizações")}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[15px] font-medium text-[#1E3A8A]">{item.views.toLocaleString(getInterfaceLanguage())}</p>
            <p className="text-[11px] text-[#6B7280]">{translateInterfaceText("visualizações")}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function DevicesTabContent({
  date,
  videoId,
  devicesData,
  metricsEnhanced
}: DevicesTabContentProps) {
  useLanguage();
  const platforms = metricsEnhanced?.platforms || [];

  // Mapear plataformas da API para o formato esperado
  const devices: DeviceData[] = platforms.map(platform => {
    let type: 'desktop' | 'mobile' | 'tablet' | 'tv' | 'other' = 'other';

    if (platform.plataforma) {
      const platformName = platform.plataforma.toLowerCase();
      if (platformName.includes('desktop') || platformName.includes('computador')) {
        type = 'desktop';
      } else if (platformName.includes('celular') || platformName.includes('mobile')) {
        type = 'mobile';
      } else if (platformName.includes('tablet')) {
        type = 'tablet';
      } else if (platformName.includes('tv')) {
        type = 'tv';
      }
    }

    return {
      type,
      views: platform.views,
      percentage: parseFloat(platform.percentual)
    };
  }).sort((a, b) => b.views - a.views); // Ordenar do maior para o menor

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        {/* Gráfico Donut Premium */}
        <PremiumDonutChart
          title={translateInterfaceText("Distribuição por Dispositivos")}
          data={devices.map((device: DeviceData) => ({
            name: getDeviceLabel(device.type),
            value: device.views,
            percentage: device.percentage
          }))}
          renderLegends={false}
        />
        
        {/* Detalhes por dispositivo */}
        <Card className="border border-[#E2E8FF] rounded-[14px] bg-white p-[22px] min-h-[260px]">
          <h3 className="text-[16px] font-semibold text-[#1A1A1A] mb-[18px]">{translateInterfaceText("Detalhes por Dispositivo")}</h3>
          <DevicesList data={devices} />
        </Card>
      </div>
      

      <div className="mt-0">
        <DevicesMetricsTable data={platforms} pitches={metricsEnhanced?.videoInfo?.pitches ?? []} />
      </div>
    </div>
  );
}

